import { db } from '../db/index.js';
import { getAllStores, addStore, reorderStores } from '../db/stores.js';
import { getListItems } from '../db/articles.js';
import { pushCreateStore, joinStore, pullData } from '../api/user-sync.js';
import { openModal, closeModal } from '../components/modal.js';
import { createElement, clearElement, showToast } from '../utils/dom.js';
import { navigate } from '../router.js';
import {
  iconStore,
  iconChevronRight,
  iconPlus,
  iconSearch,
  iconGripVertical,
} from '../utils/icons.js';
import { isLoggedIn, getUser } from '../utils/auth.js';
import { requireOnline } from '../utils/network.js';

/**
 * Affiche la page d'accueil avec la liste des magasins.
 * @param {HTMLElement} container
 */
export async function renderHomePage(container) {
  clearElement(container);

  const page = createElement('div', { className: 'page page-home page--no-header page--with-fab page--with-nav' });

  try {
    const stores = await getAllStores();

    const itemCounts = await Promise.all(
      stores.map(async (store) => {
        const items = await getListItems(store.id);
        return items.length;
      }),
    );

    // Logo + greeting + rejoindre
    const user = getUser();
    const hour = new Date().getHours();
    let greeting = 'Bonjour';
    if (hour >= 18) greeting = 'Bonsoir';
    else if (hour < 6) greeting = 'Bonne nuit';
    const name = user?.pseudo ? `, ${user.pseudo.split(' ')[0]}` : '';

    const heroChildren = [
      createElement('img', {
        className: 'page-home__logo',
        attrs: { src: '/images/logo-text.png', alt: 'GroceWise', draggable: 'false' },
      }),
      createElement('h2', {
        className: 'page-home__greeting',
        text: `${greeting}${name} 👋`,
      }),
      createElement('p', {
        className: 'page-home__tagline',
        text: stores.length === 0
          ? 'Commencez par ajouter votre premier magasin !'
          : `${stores.length} magasin${stores.length > 1 ? 's' : ''} • ${itemCounts.reduce((a, b) => a + b, 0)} articles`,
      }),
    ];

    // Bandeau « Rejoindre une liste » — discret, sous le tagline
    if (isLoggedIn()) {
      heroChildren.push(createElement('button', {
        className: 'page-home__join-banner',
        attrs: { type: 'button' },
        children: [
          iconSearch(14),
          createElement('span', { text: 'Rejoindre une liste partagée' }),
          iconChevronRight(14),
        ],
        events: {
          click: () => openJoinListModal(() => renderHomePage(container)),
        },
      }));
    }

    const heroSection = createElement('div', {
      className: 'page-home__hero',
      children: heroChildren,
    });
    page.appendChild(heroSection);

    if (stores.length === 0) {
      // Empty state avec emplacement illustration
      page.appendChild(createElement('div', {
        className: 'empty-state',
        children: [
          createElement('img', {
            className: 'page-home__illustration',
            attrs: { src: '/images/empty-basket.jpg', alt: 'Panier de courses vide', loading: 'lazy' },
          }),
          createElement('h2', { className: 'empty-state__title', text: 'Votre liste est vide' }),
          createElement('p', {
            className: 'empty-state__message',
            text: 'Appuyez sur + pour créer votre premier magasin et commencer à organiser vos courses.',
          }),
        ],
      }));
    } else {
      // Liste des magasins
      const storesList = createElement('div', { className: 'page-home__stores' });

      for (let i = 0; i < stores.length; i++) {
        const store = stores[i];
        const count = itemCounts[i];
        const meta = count === 0
          ? 'Liste vide'
          : `${count} article${count > 1 ? 's' : ''}`;

        const dragHandle = createElement('div', {
          className: 'page-home__drag-handle',
          attrs: { 'aria-label': 'Maintenir pour déplacer' },
          children: [iconGripVertical(18)],
        });

        const card = createElement('div', {
          className: 'card card--store',
          attrs: { 'data-store-id': String(store.id) },
          children: [
            dragHandle,
            createElement('span', { className: 'card--store__icon', children: [iconStore()] }),
            createElement('div', {
              className: 'card--store__content',
              children: [
                createElement('div', { className: 'card--store__name', text: store.name }),
                createElement('div', { className: 'card--store__meta', text: meta }),
              ],
              events: { click: () => navigate(`/store/${store.id}`) },
            }),
            createElement('span', {
              className: 'card--store__chevron',
              children: [iconChevronRight(20)],
              events: { click: () => navigate(`/store/${store.id}`) },
            }),
          ],
        });

        storesList.appendChild(card);
      }

      setupStoreDragDrop(storesList, () => renderHomePage(container));
      page.appendChild(storesList);

      // Bannière illustrée en bas
      const tipBanner = createElement('div', {
        className: 'page-home__tip-banner',
        children: [
          createElement('img', {
            className: 'page-home__tip-img',
            attrs: { src: '/images/shopping-hero.jpg', alt: 'Faire ses courses', loading: 'lazy' },
          }),
          createElement('div', {
            className: 'page-home__tip-text',
            children: [
              createElement('strong', { text: 'Astuce' }),
              createElement('p', { text: 'Maintenez un magasin pour le réorganiser dans l\'ordre de vos courses !' }),
            ],
          }),
        ],
      });
      page.appendChild(tipBanner);
    }

    // FAB ajouter magasin
    const fab = createElement('button', {
      className: 'btn btn--fab',
      attrs: { type: 'button', 'aria-label': 'Ajouter un magasin' },
      children: [iconPlus(24)],
      events: { click: () => openAddStoreModal(() => renderHomePage(container)) },
    });

    container.appendChild(page);
    container.appendChild(fab);
  } catch (err) {
    showToast(err.message || 'Erreur lors du chargement', 3000, 'error');
    container.appendChild(page);
  }
}

/**
 * Configure le drag & drop tactile pour réordonner les magasins.
 */
function setupStoreDragDrop(list, refresh) {
  let draggedEl = null;
  let placeholder = null;
  let startY = 0;
  let startTop = 0;

  function onTouchStart(e) {
    const grip = e.target.closest('.page-home__drag-handle');
    if (!grip) return;
    draggedEl = grip.closest('.card--store');
    if (!draggedEl) return;

    const rect = draggedEl.getBoundingClientRect();
    startY = e.touches[0].clientY;
    startTop = rect.top;

    placeholder = createElement('div', { className: 'page-home__store-placeholder' });
    placeholder.style.height = rect.height + 'px';

    draggedEl.classList.add('card--store--dragging');
    draggedEl.style.width = rect.width + 'px';
    draggedEl.style.position = 'fixed';
    draggedEl.style.top = rect.top + 'px';
    draggedEl.style.left = rect.left + 'px';
    draggedEl.style.zIndex = '1000';

    draggedEl.parentNode.insertBefore(placeholder, draggedEl);
    e.preventDefault();
  }

  function onTouchMove(e) {
    if (!draggedEl) return;
    e.preventDefault();
    const currentY = e.touches[0].clientY;
    draggedEl.style.top = (startTop + (currentY - startY)) + 'px';

    const siblings = Array.from(list.querySelectorAll('.card--store:not(.card--store--dragging)'));
    for (const sibling of siblings) {
      const sibRect = sibling.getBoundingClientRect();
      if (currentY < sibRect.top + sibRect.height / 2) {
        list.insertBefore(placeholder, sibling);
        return;
      }
    }
    list.appendChild(placeholder);
  }

  async function onTouchEnd() {
    if (!draggedEl) return;
    draggedEl.classList.remove('card--store--dragging');
    draggedEl.style.position = '';
    draggedEl.style.top = '';
    draggedEl.style.left = '';
    draggedEl.style.width = '';
    draggedEl.style.zIndex = '';

    if (placeholder?.parentNode) {
      placeholder.parentNode.insertBefore(draggedEl, placeholder);
      placeholder.remove();
    }

    const ids = Array.from(list.querySelectorAll('.card--store'))
      .map((el) => Number(el.dataset.storeId));
    draggedEl = null;
    placeholder = null;

    try {
      await reorderStores(ids);
    } catch (err) {
      showToast(err.message || 'Erreur', 3000, 'error');
      refresh();
    }
  }

  list.addEventListener('touchstart', onTouchStart, { passive: false });
  list.addEventListener('touchmove', onTouchMove, { passive: false });
  list.addEventListener('touchend', onTouchEnd);
}

/**
 * Ouvre la modale de création de magasin.
 */
function openAddStoreModal(onSuccess) {
  if (!requireOnline()) return;

  const nameInput = createElement('input', {
    className: 'input',
    attrs: { type: 'text', placeholder: 'Nom du magasin', autofocus: 'autofocus' },
  });

  const form = createElement('div', {
    className: 'input-group',
    children: [
      createElement('label', { className: 'input-group__label', text: 'Nom du magasin' }),
      nameInput,
    ],
  });

  const submitBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Créer',
    events: {
      click: async () => {
        try {
          const name = nameInput.value.trim();
          if (!name) {
            showToast('Le nom est requis', 3000, 'error');
            return;
          }
          const newStoreId = await addStore(name);
          const allStores = await getAllStores();
          const newStore = allStores.find((s) => s.id === newStoreId);
          const serverResult = await pushCreateStore(name, newStore?.order ?? 0);
          if (serverResult?.id) {
            await db.stores.update(newStoreId, { serverId: serverResult.id });
          }
          closeModal();
          showToast('Magasin créé', 2000, 'success');
          onSuccess();
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  openModal({
    title: 'Nouveau magasin',
    content: form,
    footer: submitBtn,
  });

  nameInput.focus();
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submitBtn.click();
  });
}

/**
 * Ouvre la modale pour rejoindre une liste partagée (compte requis).
 */
function openJoinListModal(onSuccess) {
  if (!requireOnline()) return;
  if (!isLoggedIn()) {
    showToast('Connectez-vous pour rejoindre une liste', 3000, 'error');
    navigate('/login');
    return;
  }

  const codeInput = createElement('input', {
    className: 'input',
    attrs: {
      type: 'text',
      placeholder: 'Code à 8 caractères',
      maxlength: '8',
      autocapitalize: 'characters',
      autofocus: 'autofocus',
    },
  });

  const form = createElement('div', {
    className: 'input-group',
    children: [
      createElement('label', { className: 'input-group__label', text: 'Code de partage' }),
      codeInput,
    ],
  });

  const joinBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Rejoindre',
    events: {
      click: async () => {
        try {
          const code = codeInput.value.trim().toUpperCase();
          if (code.length !== 8) {
            showToast('Le code doit faire 8 caractères', 3000, 'error');
            return;
          }
          joinBtn.disabled = true;
          joinBtn.textContent = 'Connexion…';

          await joinStore(code);
          await pullData();
          closeModal();
          showToast('Liste rejointe !', 2000, 'success');
          onSuccess();
        } catch (err) {
          joinBtn.disabled = false;
          joinBtn.textContent = 'Rejoindre';
          showToast(err.message || 'Code invalide ou liste introuvable', 3000, 'error');
        }
      },
    },
  });

  openModal({
    title: 'Rejoindre une liste',
    content: form,
    footer: joinBtn,
  });

  codeInput.focus();
  codeInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') joinBtn.click();
  });
}
